import { HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  ApiClientService,
  IBooking,
  IBookingListParams,
  IBookingReasonPayload,
  ICancelBookingResult,
  ICompleteBookingPayload,
  ICreateBookingPayload,
  ICreateBookingResult,
  IPaginatedResponse,
  IProposeBookingTimePayload,
  TENANT_API
} from '@pharvio/core/shared';
import { Observable } from 'rxjs';
import { BookingsDataService } from './repositories/bookings-data.service';

@Injectable()
export class HttpBookingsDataService extends BookingsDataService {
  private readonly api = inject(ApiClientService);

  getBookings(
    params: IBookingListParams = {}
  ): Observable<IPaginatedResponse<IBooking>> {
    let httpParams = new HttpParams();
    if (params.page) httpParams = httpParams.set('page', params.page);
    if (params.size) httpParams = httpParams.set('size', params.size);
    if (params.search) httpParams = httpParams.set('search', params.search);
    // `status` repeats rather than joining: the BE DTO coerces a single value
    // to an array, so `?status=a&status=b` is the only multi-value form.
    for (const status of params.status ?? []) {
      httpParams = httpParams.append('status', status);
    }
    if (params.branchId) {
      httpParams = httpParams.set('branchId', params.branchId);
    }
    if (params.customerId) {
      httpParams = httpParams.set('customerId', params.customerId);
    }
    if (params.bookableServiceId) {
      httpParams = httpParams.set(
        'bookableServiceId',
        params.bookableServiceId
      );
    }
    if (params.from) httpParams = httpParams.set('from', params.from);
    if (params.to) httpParams = httpParams.set('to', params.to);

    return this.api.get<IPaginatedResponse<IBooking>>(
      TENANT_API.BOOKINGS.BASE,
      httpParams
    );
  }

  getBooking(id: string): Observable<IBooking> {
    return this.api.get<IBooking>(TENANT_API.BOOKINGS.BY_ID(id));
  }

  createBooking(
    payload: ICreateBookingPayload
  ): Observable<ICreateBookingResult> {
    return this.api.post<ICreateBookingResult>(
      TENANT_API.BOOKINGS.BASE,
      payload
    );
  }

  confirm(id: string): Observable<IBooking> {
    return this.api.post<IBooking>(TENANT_API.BOOKINGS.CONFIRM(id), {});
  }

  proposeTime(
    id: string,
    payload: IProposeBookingTimePayload
  ): Observable<IBooking> {
    return this.api.post<IBooking>(
      TENANT_API.BOOKINGS.PROPOSE_TIME(id),
      payload
    );
  }

  start(id: string): Observable<IBooking> {
    return this.api.post<IBooking>(TENANT_API.BOOKINGS.START(id), {});
  }

  complete(
    id: string,
    payload: ICompleteBookingPayload
  ): Observable<ICreateBookingResult> {
    return this.api.post<ICreateBookingResult>(
      TENANT_API.BOOKINGS.COMPLETE(id),
      payload
    );
  }

  decline(
    id: string,
    payload: IBookingReasonPayload
  ): Observable<ICancelBookingResult> {
    return this.api.post<ICancelBookingResult>(
      TENANT_API.BOOKINGS.DECLINE(id),
      payload
    );
  }

  noShow(id: string): Observable<ICancelBookingResult> {
    return this.api.post<ICancelBookingResult>(
      TENANT_API.BOOKINGS.NO_SHOW(id),
      {}
    );
  }

  cancel(
    id: string,
    payload: IBookingReasonPayload
  ): Observable<ICancelBookingResult> {
    return this.api.post<ICancelBookingResult>(
      TENANT_API.BOOKINGS.CANCEL(id),
      payload
    );
  }
}
